import {Component, inject, OnInit} from '@angular/core';
import {CommonModule, NgClass} from '@angular/common';
import {Router, RouterModule} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {CommonService} from '../../../core/api/services/utility-services/common.service';
import {SessionDto} from '../../../core/models/session/sessiondto';
import {UrlConstants} from '../../../core/constants/url.constants';
import {SidePanelService} from '../../common/side-panel/side-panel.service';
import {FormType} from '../../../core/constants/form-type.constants';
import {LocaleService} from '../../../core/services/locale.service';
import {PartialFeaturedResource} from '../../../core/models/featuredResources/partial-featured-resource';
import {EventLogReferrerConstants} from '../../../core/constants/event-log-referrer.constants';
import {RoleConstants} from '../../../core/constants/role.constants';

@Component({
  selector: 'app-partner-home',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe, NgClass],
  templateUrl: './partner-home.component.html',
  styleUrl: './partner-home.component.css'
})
export class PartnerHomeComponent implements OnInit {
  sessionDetails: SessionDto;
  partialFeatureResource: PartialFeaturedResource[] = [];
  showFeaturedResource: boolean = false;
  readonly commonService = inject(CommonService);
  readonly translateService = inject(TranslateService);
  readonly router = inject(Router);
  readonly sidePanelService = inject(SidePanelService);
  readonly localeService = inject(LocaleService);

  ngOnInit() {
    this.commonService.sessiondetails?.subscribe(res => {
      this.sessionDetails = res;
    });

    this.commonService.getPartialFeatureResources().subscribe({
      next: (res) => {
        this.partialFeatureResource = res.partialFeatureResources;
        this.showFeaturedResource = res.showFeaturedResource;
      },
      error: (err) => {
        console.error('Error fetching promotional content:', err);
        this.partialFeatureResource = [];
        this.showFeaturedResource = false;
      }
    });
  }

  accessible(role: string): boolean {
    return this.sessionDetails && this.sessionDetails.userRoles?.includes(role);
  }

  hasAccess(roles: string | string[]): boolean {
    if (typeof roles === 'string') {
      return this.accessible(roles);
    }
    return roles.some(role => this.accessible(role));
  }

  hasProgramModels(): boolean {
    return this.sessionDetails?.hasProgramModels || false;
  }

  // Navigation methods
  navigateToDashboard(): void {
    this.router.navigate([UrlConstants.dashboardPartnerUrl,{ referrer: EventLogReferrerConstants.HOMEPAGE}]);
  }

  navigateToCommunications(): void {
    this.router.navigate(['/partner/communications', { referrer: EventLogReferrerConstants.HOMEPAGE}]);
  }

  navigateToPromotions(): void {
    this.router.navigate(['/partner/promotions', { referrer: EventLogReferrerConstants.HOMEPAGE}]);
  }

  navigateToAccounts(): void {
    this.router.navigate(['/accounts', { referrer: EventLogReferrerConstants.HOMEPAGE}]);
  }

  navigateToSalesOffice(): void {
    this.router.navigate(['/account-sales', { referrer: EventLogReferrerConstants.HOMEPAGE}]);
  }

  navigateToUsers(): void {
    this.router.navigate(['/accounts/users', { referrer: EventLogReferrerConstants.HOMEPAGE}]);
  }

  navigateToPrograms(): void {
    this.router.navigate(['/programs', { referrer: EventLogReferrerConstants.HOMEPAGE}]);
  }

  navigateToTrainingCatalog(): void {
    this.sidePanelService.open({
      formType: FormType.TRAINING,
      title: '',
      referrer: EventLogReferrerConstants.HOMEPAGE
    });
  }

  navigateToCISM(): void {
    window.open('/crcWeb/file/' + this.localeService.getLocaleStr('_') + '/featured-items/Home-Ads/CRC_CISMFlyer.pdf', '_blank');
  }

  navigateToContactUs(): void {
    this.router.navigate(['/partner/contactUs', { referrer: EventLogReferrerConstants.HOMEPAGE}]);
  }

  getEmptyBoxesCount(): number[] {
    const count = Math.max(0, 4 - this.partialFeatureResource.length);
    return Array(count).fill(0).map((_, i) => i);
  }

  getCommunicationsWords(): string[] {
    const text = this.translateService.instant('homepage.dashboard.coreCommunications');
    return text.split(/ (.+)/);
  }

  getEducationalMaterialWords(): string[] {
    const text = this.translateService.instant('homepage.dashboard.educationalMaterial');
    return text.split(/ (.+)/);
  }
  openPromotionalContent(resource: PartialFeaturedResource): void {
    if (resource && resource.url) {
      window.open(resource.url, '_blank');
    }
  }

  protected readonly RoleConstants = RoleConstants;
}

